<template>
  <v-row id="capecDetails">
    <v-col cols="12">
      <h4 class="overline mb-1">Attack Pattern Details</h4>
      <v-card flat color="bg_darkerish">
        <v-card-text class="py-6 px-6" v-if="selectedCAPEC && selectedCAPEC.id">
          <p class="text-h6 font-weight-light white--text">
            <span class="dw_green--text mr-2">CAPEC-{{ selectedCAPEC.id }}</span>
            {{ selectedCAPEC.name }}
            <v-chip
              label
              outlined
              x-small
              :color="selectedCAPEC.status === 'Deprecated' ? 'red accent-2': 'grey'"
              class="ml-2 text-uppercase">{{ selectedCAPEC.status }}</v-chip>
          </p>
          <p class="body-2 font-weight-light">
            {{ selectedCAPEC.description }}
          </p>
          <v-divider class="my-2"></v-divider>

          <div v-if="selectedCAPEC.parent">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>CAPEC&trade; Parent Attack Pattern</span>
            </p>
            <v-chip
              label
              outlined
              small
              color="dw_green"
              class="my-2"
              @click="$router.push({
                name: 'capec_view',
                params: { id: selectedCAPEC.parent } })"
            >
              CAPEC-{{ selectedCAPEC.parent }}
            </v-chip>
            <v-divider class="my-2"></v-divider>
          </div>

          <div v-if="selectedCAPEC.next_related">
            <p class="text-subtitle-1 font-weight-regular white--text mb-0">
              <span>Precedes</span>
            </p>
            <v-chip
              label
              outlined
              small
              color="dw_green"
              class="my-2"
              @click="$router.push({
                name: 'capec_view',
                params: { id: selectedCAPEC.next_related} })"
            >
              CAPEC-{{ selectedCAPEC.next_related }}
            </v-chip>
            <v-divider class="my-2"></v-divider>
          </div>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Alternate Terms
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.alternate_terms.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else v-for="(alt, index) in selectedCAPEC.alternate_terms"
              :key="`alt.${index}`">
              <li v-for="(altB, indexB) in alt" :key="`altB.${indexB}`">
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                {{ altB[1] }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Likelihood |
            <span class="font-weight-light grey--text">
              {{ selectedCAPEC.likelihood === '' ? '--' : selectedCAPEC.likelihood }}
            </span>
          </p>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Severity |
            <span class="font-weight-light grey--text">
              {{ selectedCAPEC.severity === '' ? '--' : selectedCAPEC.severity }}
            </span>
          </p>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Execution Flow
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.execution_flow.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div
              v-else
              v-for="(step, index) in selectedCAPEC.execution_flow"
              :key="`step.${index}`"
              class="ml-4">
              <p class="font-weight-regular grey--text mb-0">
                <span class="font-weight-regular white--text">Step {{ step[0][1] }}</span>
                - {{ step[1][1] }}
              </p>
              <p class="font-weight-light grey--text mb-0">
                {{ step[2][1] }}
              </p>
              <div v-for="(flow, indexB) in step" :key="`flow.${indexB}`">
                <li v-if="indexB > 2">
                  <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                  <span class="text-capitalize">{{ flow[0].toLowerCase(0) }}:</span> {{ flow[1] }}
                </li>
              </div>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Prerequisites
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.prerequisites.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li v-else v-for="(prq, index) in selectedCAPEC.prerequisites" :key="`prq.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ prq }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Required Skills
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.required_skills.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li v-else v-for="(skl, index) in selectedCAPEC.required_skills" :key="`skl.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              <span class="text-capitalize">{{ skl[1][1].toLowerCase(0) }}:</span> {{ skl[0][1] }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Required Resources
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.required_resources.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li
              v-else
              v-for="(rsc, index) in selectedCAPEC.required_resources"
              :key="`rsc.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ rsc }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Indicators
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.indicators.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li v-else v-for="(indic, index) in selectedCAPEC.indicators" :key="`indic.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ indic }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Consequences
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.consequences.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <div v-else v-for="(cnsq, index) in selectedCAPEC.consequences" :key="`cnsq.${index}`">
              <li v-for="(cnsqB, indexB) in cnsq" :key="`cnsqB.${indexB}`">
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="text-capitalize">{{ cnsqB[0].toLowerCase(0) }}:</span> {{ cnsqB[1] }}
              </li>
            </div>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Mitigations
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.mitigations.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li v-else v-for="(mit, index) in selectedCAPEC.mitigations" :key="`mit.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ mit }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Examples
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.examples.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li v-else v-for="(example, index) in selectedCAPEC.examples" :key="`example.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ example }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Taxonomy Mappings</span>
          </p>
          <div
            v-for="(tax, index) in taxonomyMappings"
            :key="`taxo.${index}`">
            <!-- taxonomyMappings[0] is always an array -->
            <div v-if="index === 0">
              <v-chip-group column v-if="tax.length > 0">
                <v-chip
                  v-for="(teq, indexB) in tax"
                  :key="`teqtaxo.${indexB}`"
                  label
                  outlined
                  small
                  color="dw_green"
                  @click="queryTechnique(teq['ENTRY ID'])"
                >
                  {{ teq['ENTRY ID'] }}
                </v-chip>
              </v-chip-group>
            </div>
            <!-- taxonomyMappings.length > 1 is any other types of mappings -->
            <ul class="font-weight-light" v-else>
              <li v-if="taxonomyMappings.length === 1">
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                --
              </li>
              <li v-else>
                <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                <span class="white--text text-decoration-underline">
                  {{ tax['TAXONOMY NAME'] }}
                </span>
                <span class="ml-1">-</span>
                <!-- TODO: are there other fields?, need more examples -->
                <span v-if="tax['ENTRY ID']" class="ml-1">{{ tax['ENTRY ID'] }}</span>
                <span v-if="tax['ENTRY NAME']" class="ml-1">{{ tax['ENTRY NAME'] }}</span>
              </li>
            </ul>
          </div>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Notes
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedCAPEC.notes.length === 0">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              --
            </li>
            <li v-else v-for="(note, index) in selectedCAPEC.notes" :key="`note.${index}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ note }}
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related CWE&trade; Weaknesses</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedCAPEC.related_weaknesses.length === 0"
              label
              outlined
              small
              color="grey">No Related Weaknesses</v-chip>
            <v-chip
              v-else
              v-for="cwe in selectedCAPEC.related_weaknesses"
              :key="`cwe.${cwe}`"
              label
              outlined
              small
              color="dw_green"
              @click="$router.push({ name: 'cwe_view', params: { id: cwe } })"
            >
              CWE-{{ cwe }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>Related Plays</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="!selectedCAPEC.related_plays || selectedCAPEC.related_plays.length === 0"
              label
              outlined
              small
              color="grey">No Related Plays</v-chip>
            <v-tooltip
              bottom
              v-else
              v-for="ply in selectedCAPEC.related_plays"
              :key="`ply.${ply.id}`">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  label
                  outlined
                  small
                  color="dw_green"
                  v-bind="attrs"
                  v-on="on"
                  @click="$router.push({ name: 'play_view', params: { id: ply.id }})">
                  {{ ply.id }}
                </v-chip>
              </template>
              <span>{{ ply.name }}</span>
            </v-tooltip>
          </v-chip-group>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { mdiSquareMediumOutline } from '@mdi/js';
import apiService from '@/services/api.service';

export default {
  name: 'capecDetails',
  data() {
    return {
      mdiSquareMediumOutline
    }
  },
  watch: {
    '$route.params': {
      async handler(params) {
        if (params.id) {
          await this.fetchCAPECByID(params.id);
          this.$vuetify.goTo('#capecDetails');
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      'selectedCAPEC'
    ]),
    taxonomyMappings() {
      let teqs = this.selectedCAPEC.taxonomy_mappings.filter((tax) => tax[0][1] === 'ATTACK');
      teqs = teqs.map((tax) => Object.fromEntries(tax));
      let other = this.selectedCAPEC.taxonomy_mappings.filter((tax) => tax[0][1] !== 'ATTACK')
      other = other.map((tax) => Object.fromEntries(tax));
      return [teqs, ...other];
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'capec_view') {
      this.clearSelected('capec');
    }
    next();
  },
  methods: {
    ...mapActions([
      'fetchCAPECByID'
    ]),
    ...mapMutations([
      'clearSelected'
    ]),
    async queryTechnique(mitreID) {
      const data = await apiService.get(`techniques/?mitre_id=${mitreID}`);
      if (data.results.length === 1) {
        this.$router.push({ name: 'technique_view', params: { id: data.results[0].id } });
      } else {
        this.$router.push({ name: 'techniques', hash: `#T${mitreID}` })
      }
    }
  }
};
</script>

<style scoped>
ul {
  padding: 0 0 0 0;
}
li {
  list-style-type: none;
}
</style>
